/**
 * @fileoverview AiUsageCard — Billing settings AI usage panel
 *
 * Drops into Settings → Billing. Shows:
 * - Current period totals + remaining budget (progress bar).
 * - Per-surface breakdown (universal chat, summary chat, contact
 *   chat, precedent AI, managed agents, canvas) so the user can see
 *   WHERE the cost is going.
 * - Status chip — within budget / soft warning / capped.
 *
 * Reactive via `getOrgUsageBreakdown`. Plain read-only — the user can't
 * adjust the cap from here (contact-support flow per the planning
 * doc).
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  AlertTriangle,
  CheckCircle2,
  OctagonAlert,
  Sparkles,
  MessageSquare,
  Contact,
  FileStack,
  Database,
  FileText,
  Loader2,
} from "lucide-react";
import { cn } from "@/utils/misc";

interface AiUsageCardProps {
  orgId: Id<"organizations">;
  className?: string;
}

const SURFACE_META: Record<
  string,
  { label: string; icon: typeof Sparkles; description: string }
> = {
  universal_chat: {
    label: "Workspace + Job Agent",
    icon: Sparkles,
    description: "Universal chat across the workspace and per-job Agent tab.",
  },
  summary_chat: {
    label: "Summary Q&A",
    icon: MessageSquare,
    description: "Chat sheet on each data agent's summary tab.",
  },
  contact_chat: {
    label: "Contact Chat",
    icon: Contact,
    description: "Per-contact chat in the CRM.",
  },
  precedent_chat: {
    label: "Precedent AI Editor",
    icon: FileStack,
    description: "AI rewrites in the precedent template editor.",
  },
  managed_agent: {
    label: "Data Agents",
    icon: Database,
    description:
      "Background summarisation agents (assets, related parties, etc.).",
  },
  canvas: {
    label: "Canvas",
    icon: FileText,
    description: "AI-authored reports in the Canvas side panel.",
  },
};

function formatUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatPeriodLabel(yyyyMm: string): string {
  // "2026-05" → "May 2026"
  const [yyyy, mm] = yyyyMm.split("-").map((s) => parseInt(s, 10));
  if (!yyyy || !mm) return yyyyMm;
  const d = new Date(Date.UTC(yyyy, mm - 1, 1));
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export function AiUsageCard({ orgId, className }: AiUsageCardProps) {
  const { data, isLoading } = useQuery(
    convexQuery(api.lib.aiUsageQueries.getOrgUsageBreakdown, { orgId }),
  );

  if (isLoading) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-xl border border-border bg-background p-6 shadow-sm",
          className,
        )}
      >
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  const pct = Math.min(
    100,
    Math.round((data.totalUsdCents / data.capUsdCents) * 100),
  );
  const remaining = Math.max(0, data.capUsdCents - data.totalUsdCents);

  // Sort surfaces by spend desc so the heaviest contributor is at the top.
  const sortedSurfaces = [...data.perSurface].sort(
    (a, b) => b.usdCents - a.usdCents,
  );

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-border bg-background shadow-sm",
        className,
      )}
    >
      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1.5">
            <h2 className="text-xl font-medium text-primary">AI Usage</h2>
            <p className="text-sm text-primary/60">
              Morgan caps total AI spend at{" "}
              <span className="font-medium">{formatUsd(data.capUsdCents)}</span>{" "}
              per organisation per month while we firm up usage-based pricing.{" "}
              {formatPeriodLabel(data.periodYearMonth)}.
            </p>
          </div>
          <StatusBadge hardBlock={data.hardBlock} softWarn={data.softWarn} />
        </div>
      </div>

      {/* Progress */}
      <div className="space-y-2 px-6 pb-4">
        <div className="flex items-baseline justify-between">
          <p className="text-3xl font-semibold tracking-tight">
            {formatUsd(data.totalUsdCents)}
          </p>
          <p className="text-sm text-muted-foreground">
            of {formatUsd(data.capUsdCents)} ·{" "}
            <span className="font-medium text-foreground">
              {formatUsd(remaining)}
            </span>{" "}
            remaining
          </p>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              "h-full rounded-full transition-all",
              data.hardBlock
                ? "bg-destructive"
                : data.softWarn
                  ? "bg-amber-500"
                  : "bg-primary",
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Per-surface breakdown */}
      <div className="border-t border-border/50 bg-muted/20 px-6 py-4">
        <p className="mb-3 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
          Where this month's spend is going
        </p>
        {sortedSurfaces.length === 0 ? (
          <p className="text-xs text-muted-foreground/70">
            No AI usage recorded yet this month.
          </p>
        ) : (
          <ul className="space-y-2">
            {sortedSurfaces.map((s) => {
              const meta = SURFACE_META[s.surface] ?? {
                label: s.surface,
                icon: Sparkles,
                description: "",
              };
              const Icon = meta.icon;
              const surfacePct =
                data.totalUsdCents > 0
                  ? Math.round((s.usdCents / data.totalUsdCents) * 100)
                  : 0;
              return (
                <li
                  key={s.surface}
                  className="flex items-start gap-3 rounded-lg border border-border/40 bg-background px-3 py-2.5"
                >
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="truncate text-sm font-medium text-foreground">
                        {meta.label}
                      </p>
                      <p className="shrink-0 text-sm font-medium text-foreground">
                        {formatUsd(s.usdCents)}
                      </p>
                    </div>
                    <p className="text-[11px] text-muted-foreground/70">
                      {meta.description}
                    </p>
                    <p className="mt-0.5 text-[10px] text-muted-foreground/50">
                      {s.inputTokens.toLocaleString()} in /{" "}
                      {s.outputTokens.toLocaleString()} out tokens
                      {s.cacheReadTokens > 0
                        ? ` · ${s.cacheReadTokens.toLocaleString()} cache reads`
                        : ""}{" "}
                      · {surfacePct}% of spend
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="flex min-h-14 w-full items-center justify-between border-t border-border/50 bg-secondary/50 px-6 py-3">
        <p className="text-xs text-primary/60">
          Need a higher monthly cap? Contact Morgan support.
        </p>
        <a
          href="mailto:support@morgan.com.au?subject=AI%20usage%20cap%20uplift"
          className="text-xs font-medium text-primary hover:underline"
        >
          Contact support
        </a>
      </div>
    </div>
  );
}

function StatusBadge({
  hardBlock,
  softWarn,
}: {
  hardBlock: boolean;
  softWarn: boolean;
}) {
  if (hardBlock) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-destructive/40 bg-destructive/10 px-2.5 py-1 text-[11px] font-medium text-destructive">
        <OctagonAlert className="h-3 w-3" />
        Cap reached
      </span>
    );
  }
  if (softWarn) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[11px] font-medium text-amber-700 dark:text-amber-300">
        <AlertTriangle className="h-3 w-3" />
        Approaching cap
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
      <CheckCircle2 className="h-3 w-3" />
      Within budget
    </span>
  );
}
